import type {
  GameState,
  LabourPriority,
  RoomState,
  Space,
  Survivor,
  SurvivorActivity,
} from "../types";

const DEFAULT_LABOUR: Record<string, { priority: LabourPriority; minimum: number }> = {
  generator: { priority: "critical", minimum: 1 },
  dormitory: { priority: "deferred", minimum: 0 },
  infirmary: { priority: "critical", minimum: 1 },
  entrance: { priority: "critical", minimum: 1 },
  security_post: { priority: "critical", minimum: 1 },
  water_recycler: { priority: "essential", minimum: 1 },
  hydroponics: { priority: "essential", minimum: 1 },
  kitchen: { priority: "essential", minimum: 1 },
  armoury: { priority: "essential", minimum: 1 },
  radio: { priority: "normal", minimum: 0 },
  storage: { priority: "normal", minimum: 0 },
  workshop: { priority: "deferred", minimum: 0 },
  reclamation_shaft: { priority: "deferred", minimum: 0 },
};

export function roomLabourDefaults(roomType: string): { priority: LabourPriority; minimum: number } {
  return DEFAULT_LABOUR[roomType] ?? { priority: "normal", minimum: 0 };
}

export function survivorDutySpaceId(survivor: Survivor): string | null {
  if (survivor.duty?.kind === "room") return survivor.duty.id;
  return survivor.assignment?.kind === "room" ? survivor.assignment.id : null;
}

export function resolvedSurvivorActivity(survivor: Survivor): SurvivorActivity {
  if (survivor.current_activity) return survivor.current_activity;
  if (!survivor.alive || survivor.status === "dead") return { kind: "dead" };
  if (survivor.status === "away" || survivor.assignment?.kind === "expedition") {
    return { kind: "away", expedition_id: survivor.assignment?.id ?? null };
  }
  if (survivor.assignment?.kind === "task") {
    return { kind: "task_work", task_id: survivor.assignment.id };
  }
  const roomId = survivorDutySpaceId(survivor);
  if (roomId) return { kind: "room_work", room_id: roomId };
  return { kind: "idle" };
}

export function isSurvivorAvailableForTemporaryWork(survivor: Survivor): boolean {
  if (!survivor.alive || survivor.status !== "home") return false;
  const activity = resolvedSurvivorActivity(survivor).kind;
  if (["away", "dead", "rest", "treatment", "task_work"].includes(activity)) return false;
  return !survivor.assignment || survivor.assignment.kind === "room";
}

export function roomName(state: GameState, spaceId: string | null | undefined): string | null {
  if (!spaceId) return null;
  const room = state.site?.spaces?.find((space) => space.id === spaceId)?.room;
  return room?.type
    ? room.type.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
    : null;
}

function returnDutyName(state: GameState, survivor: Survivor, activity: SurvivorActivity): string | null {
  return roomName(state, activity.return_duty_id ?? survivorDutySpaceId(survivor));
}

export function survivorActivityLabel(state: GameState, survivor: Survivor): string {
  const activity = resolvedSurvivorActivity(survivor);
  const targetRoom = roomName(state, activity.room_id ?? activity.facility_space_id);
  const returnRoom = returnDutyName(state, survivor, activity);
  switch (activity.kind) {
    case "dead": return "Deceased";
    case "away": return "Beyond radio range";
    case "rest": return `${activity.reason === "medical" ? "Medically unfit—awaiting treatment" : activity.rough_sleeping ? "Rough sleeping" : "Resting"}${returnRoom ? `—returns to ${returnRoom}` : ""}`;
    case "treatment": return `In treatment${returnRoom ? `—returns to ${returnRoom}` : ""}`;
    case "relief": return targetRoom ? `Covering ${targetRoom}` : "Providing relief cover";
    case "room_work": return targetRoom ? `Working in ${targetRoom}` : "On room duty";
    case "task_work": {
      const task = state.tasks.find((item) => item.id === activity.task_id);
      return task ? `${task.kind.replaceAll("_", " ")} work` : "On a work order";
    }
    default: return returnRoom ? `On call—duty: ${returnRoom}` : "Awaiting orders";
  }
}

export function survivorActivityTarget(state: GameState, survivor: Survivor): string | null {
  const activity = resolvedSurvivorActivity(survivor);
  if (activity.kind === "away" || activity.kind === "dead") return null;
  if (activity.kind === "rest" || activity.kind === "treatment") {
    return activity.facility_space_id ?? null;
  }
  if (activity.kind === "task_work") {
    return state.tasks.find((task) => task.id === activity.task_id)?.space_id ?? null;
  }
  if (activity.kind === "room_work" || activity.kind === "relief") return activity.room_id ?? null;
  return null;
}

export function survivorVisualActivity(state: GameState, survivor: Survivor): string {
  const activity = resolvedSurvivorActivity(survivor);
  if (activity.kind === "rest") return activity.reason === "medical" ? "injured" : "rest";
  if (activity.kind === "treatment") return "infirmary";
  if (activity.kind === "away" || activity.kind === "dead") return activity.kind;
  if (activity.kind === "task_work") {
    return state.tasks.find((task) => task.id === activity.task_id)?.kind ?? "labour";
  }
  if (activity.kind === "room_work" || activity.kind === "relief") {
    return state.site.spaces.find((space) => space.id === activity.room_id)?.room?.type ?? "idle";
  }
  return "idle";
}

export function roomStaffingProjection(room: RoomState): NonNullable<NonNullable<RoomState["operation"]>["staffing"]> {
  const preferred = room.operation?.staffing?.preferred_worker_ids ?? room.worker_ids ?? [];
  const active = room.operation?.staffing?.active_worker_ids ?? preferred ?? [];
  const relief = room.operation?.staffing?.relief_worker_ids ?? [];
  const minimum = room.operation?.staffing?.minimum_staff
    ?? room.minimum_staff
    ?? roomLabourDefaults(room.type).minimum;
  const status = room.operation?.staffing?.status
    ?? (minimum > 0 && active.length + relief.length < minimum ? "uncovered" : active.length + relief.length < preferred.length ? "reduced" : "covered");
  return {
    preferred_worker_ids: preferred,
    active_worker_ids: active,
    relief_worker_ids: relief,
    minimum_staff: minimum,
    status,
  };
}

export function coverageImpactWarning(
  state: GameState,
  survivor: Survivor,
  selectedForTemporaryWork: readonly string[] = [],
): string | null {
  const activity = resolvedSurvivorActivity(survivor);
  const coveredSpaceId = activity.kind === "room_work" || activity.kind === "relief"
    ? activity.room_id ?? survivorDutySpaceId(survivor)
    : survivorDutySpaceId(survivor);
  if (!coveredSpaceId) return null;
  const space = state.site?.spaces?.find((item) => item.id === coveredSpaceId);
  if (!space?.room) return null;
  const staffing = roomStaffingProjection(space.room);
  const active = new Set([...staffing.active_worker_ids, ...staffing.relief_worker_ids]);
  if (!active.has(survivor.id) || staffing.minimum_staff <= 0) return null;
  const departing = new Set([...selectedForTemporaryWork, survivor.id]);
  const remainingActive = [...active].filter((id) => !departing.has(id)).length;
  if (remainingActive >= staffing.minimum_staff) return null;
  const name = space.room.type.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
  return `Selecting ${survivor.name} would leave protected ${name} coverage short until relief is found.`;
}

export function findRoomSpace(state: GameState, spaceId: string | null | undefined): Space | undefined {
  return spaceId ? state.site.spaces.find((space) => space.id === spaceId) : undefined;
}
